// @owners { team: patients }
import { type CartItem } from '@alto/orders_api/types/v1/cart_item';
import { useCallback } from 'react';
import { useCartData } from './useCartData';
import { getCartPrescriptionDetails } from '~shared/features/cart/selectors/getCartDetails';
import { getPrescriptionsHashWithUpdatedAt } from '~shared/features/prescriptions/selectors/getPrescriptionByID';
import { useSelectorShared } from '~shared/store';
import { type Prescription } from '~shared/types/clients';

type UsePrescriptionsInCart = {
  prescriptions: Prescription[];
  prescriptionsHash: Record<number, Prescription>;
  prescriptionIDs: number[];
  isPrescriptionInCart: (prescriptionId: number | null | undefined) => boolean;
  compounds: Prescription[];
  injectables: CartItem[];
  injectableCompounds: CartItem[];
  hasInjectables: boolean;
  hasOnlyInjectableCompounds: boolean;
  hasCompounds: boolean;
  updatedAt: number;
};

export const usePrescriptionsInCartInitialState: UsePrescriptionsInCart = {
  prescriptions: [],
  prescriptionsHash: {},
  prescriptionIDs: [],
  isPrescriptionInCart: () => false,
  compounds: [],
  hasCompounds: false,
  hasInjectables: false,
  injectables: [],
  injectableCompounds: [],
  hasOnlyInjectableCompounds: false,
  updatedAt: 0,
};

export const usePrescriptionsInCart = () => {
  const { cartItems, updatedAt: cartUpdatedAt } = useCartData();
  const prescriptionsHash = useSelectorShared(getPrescriptionsHashWithUpdatedAt);

  const {
    cartPrescriptionIds,
    cartPrescriptions,
    cartCompounds,
    cartPrescriptionIdHash,
    cartInjectable,
    cartInjectableCompounds,
  } = getCartPrescriptionDetails({
    cartItems,
    prescriptionsHash,
    cartUpdatedAt,
  });
  const hasInjectables = cartInjectable.length > 0;
  const hasOnlyInjectableCompounds = hasInjectables && cartInjectable.length === cartInjectableCompounds.length;
  const hasCompounds = cartCompounds.length > 0;

  const isPrescriptionInCart = useCallback(
    (prescriptionId: number | null | undefined): boolean =>
      !!prescriptionId && !!cartPrescriptionIdHash[prescriptionId],
    [cartPrescriptionIdHash],
  );

  return {
    prescriptions: cartPrescriptions,
    prescriptionsHash: cartPrescriptionIdHash,
    prescriptionIDs: cartPrescriptionIds,
    isPrescriptionInCart,
    compounds: cartCompounds,
    injectables: cartInjectable,
    injectableCompounds: cartInjectableCompounds,
    hasInjectables,
    hasOnlyInjectableCompounds,
    hasCompounds,
    updatedAt: cartUpdatedAt,
  };
};
